import { useFormContext } from "./formElements";
import { useMemo, useState, useEffect, useCallback } from "react";
import {
  Button,
  Form,
  Row,
  Col,
  Alert,
  Card,
} from "react-bootstrap";
import { useDataProvider } from "../../providers/offline_data_provider";

export default function Schematics({ value: element }) {
  const { onChange } = useFormContext();
  const {
    protocolsSchematics: {
      schematics,
      addProtocolSchematic,
      getProtocolSchematicById,
    },
  } = useDataProvider();

  const [newFile, setNewFile] = useState(null);
  const [newDescription, setNewDescription] = useState("");
  const [error, setError] = useState("");

  const [imageUrls, setImageUrls] = useState({});   // { [id]: objectUrl }
  const [loadingMap, setLoadingMap] = useState({}); // { [id]: true }
  const [imageErrors, setImageErrors] = useState({}); // { [id]: "komunikat" }

  // value = lista zaznaczonych id
  const selectedIds = useMemo(
    () =>
      Array.isArray(element?.value)
        ? element.value.filter((id) => typeof id === "string" || typeof id === "number")
        : [],
    [element?.value]
  );

  const updateElementValue = (ids) => {
    element.value = ids;
    onChange(element);
  };

  const isSelected = (id) => selectedIds.includes(id);

  const toggleSelect = (id) => {
    if (!id) return;
    if (isSelected(id)) {
      // odznacz
      updateElementValue(selectedIds.filter((x) => x !== id));
    } else {
      // zaznacz
      updateElementValue([...selectedIds, id]);
    }
  };

  // async: pobieranie obrazu po id
  const ensureImageLoaded = useCallback(
    (id) => {
      if (!id) return;
      if (imageUrls[id]) return;     // już jest
      if (loadingMap[id]) return;    // trwa
      if (imageErrors[id]) return;   // był błąd – nie próbujemy ponownie

      setLoadingMap((prev) => ({ ...prev, [id]: true }));

      getProtocolSchematicById(id, ({ success, error: err, content }) => {
        if (success && content instanceof Blob) {
          const url = URL.createObjectURL(content);
          setImageUrls((prev) => ({
            ...prev,
            [id]: url,
          }));
        } else {
          console.error("Błąd pobierania schematu", err);
          setImageErrors((prev) => ({
            ...prev,
            [id]: "Nie udało się pobrać obrazu schematu.",
          }));
        }

        setLoadingMap((prev) => {
          const copy = { ...prev };
          delete copy[id];
          return copy;
        });
      });
    },
    [getProtocolSchematicById, imageUrls, loadingMap, imageErrors]
  );

  // ładowanie obrazów dla dostępnych schematów
  useEffect(() => {
    if (!Array.isArray(schematics)) return;
    schematics.forEach((s) => {
      if (s?.id) ensureImageLoaded(s.id);
    });
  }, [schematics, ensureImageLoaded]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setNewFile(null);
      return;
    }

    if (!file.type.startsWith("image/")) {
      setError("Dozwolone są tylko pliki graficzne (image/*).");
      setNewFile(null);
      return;
    }

    setError("");
    setNewFile(file);
  };

  const handleAddNew = async (e) => {
    e.preventDefault();
    setError("");

    if (!newFile) {
      setError("Wybierz plik graficzny.");
      return;
    }

    try {
      // addProtocolSchematic: Promise<{ id, ... }>
      const created = await addProtocolSchematic(newFile, newDescription);

      if (!created || !created.id) {
        throw new Error("Brak poprawnej odpowiedzi z addProtocolSchematic");
      }

      // zaznacz nowo utworzony schemat w value
      const newIds = [...selectedIds, created.id];
      updateElementValue(newIds);

      // pobierz obraz nowego schematu
      ensureImageLoaded(created.id);

      setNewFile(null);
      setNewDescription("");
    } catch (err) {
      console.error(err);
      setError(
        "Nie udało się dodać schematu. Sprawdź implementację addProtocolSchematic."
      );
    }
  };

  return (
    <div className="p-2">
      {error && (
        <Alert variant="danger" className="py-2">
          {error}
        </Alert>
      )}

      {/* Jedyna lista schematów z biblioteki */}
      {!Array.isArray(schematics) || schematics.length === 0 ? (
        <Alert variant="info" className="py-2">
          Brak dostępnych schematów w bibliotece.
        </Alert>
      ) : (
        <>
          <h6 className="mt-2 mb-2">Dostępne schematy:</h6>
          <Row xs={1} md={2} className="g-2">
            {schematics.map((s) => {
              const id = s.id;
              const selected = isSelected(id);
              const imgUrl = imageUrls[id];
              const loadErr = imageErrors[id];
              const isLoading = loadingMap[id] && !imgUrl && !loadErr;

              return (
                <Col key={id}>
                  <Card
                    className={
                      "h-100 " +
                      (selected
                        ? "border-success border-2 bg-light"
                        : "")
                    }
                  >


                    {isLoading && (
                      <div className="d-flex justify-content-center align-items-center py-4">
                        <span className="text-muted small">
                          Ładowanie podglądu...
                        </span>
                      </div>
                    )}

                    {loadErr && (
                      <div className="d-flex justify-content-center align-items-center py-4">
                        <span className="text-danger small">
                          {loadErr}
                        </span>
                      </div>
                    )}

                    <Card.Body className="d-flex flex-column">
                      <Card.Title className="fs-6">
                        {s.description || s.name || "Schemat"}
                      
                      </Card.Title>
                                          {/* Obraz */}
                    {imgUrl && !loadErr && (
                      <Card.Img
                        variant="top"
                        src={imgUrl}
                        alt={s.file_name || s.description || "schemat"}
                        style={{ objectFit: "contain", maxHeight: 160 }}
                      />
                    )}

                      <div className="mt-auto text-end">
                        <Button
                          size="sm"
                          onClick={() => toggleSelect(id)}
                          variant={
                            selected ? "outline-danger" : "outline-primary"
                          }
                        >
                          {selected
                            ? "Usuń z protokołu"
                            : "Dodaj do protokołu"}
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </>
      )}

      {/* Dodawanie nowego schematu */}
      <Form onSubmit={handleAddNew} className="mt-3">
        <Row className="g-2 align-items-end">
          <Col md={4}>
            <Form.Label className="mb-1">Plik schematu (image/*)</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              onChange={handleFileChange}
            />
          </Col>
          <Col md={5}>
            <Form.Label className="mb-1">Opis / tytuł schematu</Form.Label>
            <Form.Control
              type="text"
              placeholder="Krótki opis schematu"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
            />
          </Col>
          <Col md="auto">
            <Button type="submit" className="mt-4">
              Dodaj schemat
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
}
